/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2020 - 2021
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useEffect } from "react";
import { Switch, Redirect, Route, useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { ErrorBoundary } from "react-error-boundary";
import CssBaseline from "@material-ui/core/CssBaseline";
import {
  fetchGoTrackUser,
  updateLastActiveDate,
} from "../slices/goTrackUserSlice";
import { fetchFeatureFlag } from "../slices/featureFlagsSlice";
import { setFilters } from "../slices/filtersSlice";
import { setSpotFilters } from "../slices/spotFiltersSlice";
import LandingPage from "../components/LandingPage";
import Loader from "../components/Loader";
import CarrierManagementContainer from "../components/carrier-management/CarrierManagementContainer";
import GoTrackContainer from "./GoTrackContainer";
import GoSpotContainer from "./GoSpotContainer";
import TelemetryProvider from "../components/common/TelemetryProvider";
import logger from "../helpers/logger";
import { fetchUserPermissions } from "../slices/userPermissionsSlice";
import types from "../helpers/types";
import defaultSpotFilters from "../helpers/defaultSpotFilters";
import defaultGoTrackFilters from "../helpers/defaultGoTrackFilters";
import {getDefaultFilters} from "../helpers/filterUtils";
import UserManagementContainer from "../components/user-management/UserManagementContainer";
import { isAdministrator, isShipperManager } from "../helpers/goTrackUserUtils";
import setupApiInterceptors from "../helpers/setupApiInterceptors";
import defaultUserManagementFilters from "../helpers/defaultUserManagementFilters";
import { setUserManagementFilters } from "../slices/userManagementFiltersSlice";
import { resetCarrierManagementFilters } from "../slices/carrierManagementFiltersSlice";
import {initializePendo} from "../pendoInitializer";

export default function App() {
  const goTrackUser = useSelector((state) => state.goTrackUser);

  const lastActiveDateRequestStatus = useSelector(
    (state) => state.goTrackUser.updateLastActiveDateRequestStatus
  );

  const history = useHistory();
  const dispatch = useDispatch();

  useEffect(() => {
    const stopListening = history.listen(() => {
      if(history.action === 'POP'){
        const filters = getDefaultFilters();
        dispatch(setFilters( filters ? filters.filters : defaultGoTrackFilters));
        dispatch(setSpotFilters(filters ? filters.spotFilters : defaultSpotFilters));
        dispatch(setUserManagementFilters(defaultUserManagementFilters));
        dispatch(resetCarrierManagementFilters());
      }
    });
    return () => stopListening();
  }, []);

  useEffect(() => {
    setupApiInterceptors(dispatch);
  }, []);

  useEffect(() => {
    if (
      goTrackUser.goTrackUser === null &&
      goTrackUser.fetchRequestStatus !== types.requestStatuses.inProgress &&
      !window.loadingApiKey &&
      !goTrackUser.isError
    ) {
      dispatch(fetchGoTrackUser(initializePendo));
    }
  }, [dispatch, goTrackUser]);

  useEffect(() => {
    dispatch(fetchUserPermissions());
  }, []);

  useEffect(() => {
    if (
      !window.loadingApiKey &&
      lastActiveDateRequestStatus === types.requestStatuses.initial
    ) {
      dispatch(updateLastActiveDate());
    }
  }, [dispatch, lastActiveDateRequestStatus]);

  if (
    goTrackUser.fetchRequestStatus === types.requestStatuses.inProgress ||
    (!goTrackUser.goTrackUser &&
      !window.loadingApiKey) ||
    lastActiveDateRequestStatus === types.requestStatuses.inProgress
  ) {
    return <Loader size={75} />;
  } 

  const canAccessUserManagement =
    goTrackUser &&
    goTrackUser.goTrackUser &&
    (isAdministrator(goTrackUser.goTrackUser.roles) ||
      isShipperManager(goTrackUser.goTrackUser.roles));

  return (
    <ErrorBoundary fallback={<div />} onError={logger.error}>
      <CssBaseline />
      <Switch>
        <Route path="/blank">
          <></>
        </Route>
        <Route path="/tracking">
          <GoTrackContainer />
        </Route>
        <Route path="/carrier-management">
          <CarrierManagementContainer />
        </Route>
        {canAccessUserManagement && (
          <Route path="/user-management">
            <UserManagementContainer />
          </Route>
        )}
        <Route path="/availableroutes">
          <GoSpotContainer />
        </Route>
        <Route exact path="/">
          <LandingPage />
        </Route>
        <Route render={() => <Redirect to="/" />} />
      </Switch>
    </ErrorBoundary>
  );
}
